import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { Globals } from 'app/_globals';
import { NotificationService } from 'app/_services/notification.service';
import { SiteAssetsService } from 'app/_services/site-assets.service';
import DateTimeUtils from 'app/_utils/datetime.utils';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-asset-certificates',
  templateUrl: './asset-certificate.component.html',
})
export class AssetCertificateComponent implements OnInit {

  selectedAssetId: any;
  selectedAsset: any;
  selectedCertificate: any;
  role: any;
  routeRole: any;
  tabitems: any[] = [];
  errorAlerts;

  selectedSiteId: any;

  certificates: any[] = [];
  saveInProgress = false;

  addCertificateModal = false;
  addCertificateForm: FormGroup;

  replaceDocumentModal = false;
  replaceDocumentName;

  updateCertificateModal = false;
  updateCertificateForm: FormGroup;

  selectedDocument = null;

  minDateValue = new Date();
  private currentYear = new Date().getFullYear();
  public datepickerYearRange = this.currentYear + ':' + (this.currentYear + 10);

  constructor(
    public globals: Globals,
    public activatedRoute: ActivatedRoute,
    public fb: FormBuilder,
    public notificationService: NotificationService,
    private assetService: SiteAssetsService,
  ) {
    // Get Worker id from params
    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedAssetId = params.asset;
      this.selectedSiteId = params.id;
    });

    this.role = globals.user.role;
    this.routeRole = globals.user.routeRole;

    this.tabitems = [
      { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets/' + this.selectedAssetId, title: 'Back to Asset', class: 'back-link' },
      { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets/' + this.selectedAssetId + '/certificates', title: 'Certificates' },
    ];

    this.addCertificateForm = fb.group({
      title: new FormControl(null, [Validators.required]),
      expiryDate: new FormControl(null, [Validators.required]),
      lockAssetOnExpiry: new FormControl(false, [Validators.required]),
      certFile: new FormControl(null)
    });
    this.updateCertificateForm = fb.group({
      title: new FormControl(null, [Validators.required]),
      expiryDate: new FormControl(null, [Validators.required]),
      lockAssetOnExpiry: new FormControl(false, [Validators.required]),
    });

  }

  ngOnInit() {
    this.getAsset();
  }

  private getAsset() {
    this.assetService.getAssetById(this.selectedAssetId).subscribe((data: any) => {
      this.certificates = data.assetCertificates.filter((cert) => !cert.deleted);

      this.certificates
        .sort((a, b) => {
          return new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime();
        })
        .forEach(certificate => {
          certificate.expired = DateTimeUtils.isDateStrExpired(certificate.expiryDate);
        });
      this.selectedAsset = data;
    });
  }

  // Handle onRowSelect of a user
  public async onRowSelect(event) {
    this.selectedCertificate = event.data;

    const inputOptions = {
      edit: 'Edit Certificate Details',
      replaceDoc: 'Replace Certificate Document',
    };

    const { value: action } = await Swal.fire({
      titleText: this.selectedCertificate.title,
      input: 'select',
      inputOptions: inputOptions,
      inputPlaceholder: 'Select an option',
      showCancelButton: true,
    });
    if (action) {
      if (action === 'edit') {
        this.editCertificate();
      } else if (action === 'replaceDoc') {
        this.replaceDocument();
      } else {
        console.log('No valid option selected');
      }
    }
  }

  public addCertificate() {
    this.saveInProgress = false;
    this.selectedDocument = null;
    this.addCertificateForm.reset({ lockAssetOnExpiry: false });
    this.addCertificateModal = true;
  }

  public viewDocument(file) {
    this.assetService.showAssetCertificate(file.fileName);
  }
  private replaceDocument() {
    this.saveInProgress = false;
    this.replaceDocumentModal = true;
    this.replaceDocumentName = this.selectedCertificate.file.fileName;
  }

  public deleteCertificate(certificate) {
    Swal.fire({
      title: 'Delete Certificate?',
      text: 'You will not be able to revert this!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.assetService.deleteAssetCertificate(certificate.id).subscribe(() => {
          this.notificationService.notify('success', 'Successfully Deleted');
          this.getAsset();
        });
      }
    });
  }

  private editCertificate() {
    this.updateCertificateForm.patchValue({
      title: this.selectedCertificate.title,
      expiryDate: DateTimeUtils.timelessDateStrAsUTCDate(this.selectedCertificate.expiryDate),
      lockAssetOnExpiry: this.selectedCertificate.lockAssetOnExpiry,
    });
    this.updateCertificateModal = true;
  }

  public onFileChange(event) {
    if (event.target.files.length > 0) {
      this.selectedDocument = event.target.files[0];
    }
  }

  public onReplaceFile(event) {
    if (event.target.files.length > 0) {
      this.selectedDocument = event.target.files[0];
      this.replaceDocumentName = this.selectedDocument.name;
    }
  }

  public onReplaceDocumentSubmit() {
    this.saveInProgress = true;

    this.assetService.replaceAssetCertificateDocument(this.selectedCertificate.id, this.selectedDocument).subscribe((response) => {
      this.getAsset();
      Swal.fire('Success!', 'Asset Certificate document been updated.', 'success');
      this.replaceDocumentModal = false;
      this.saveInProgress = false;
    });
  }

  public async onAddCertificateFormSubmit() {
    this.saveInProgress = true;
    const values = this.addCertificateForm.value;

    const body = {
      assetId: this.selectedAssetId,
      title: values.title,
      expiryDate: DateTimeUtils.timelessDateAsUTCDate(values.expiryDate).toISOString(),
      lockAssetOnExpiry: values.lockAssetOnExpiry,
      certFile: this.selectedDocument
    };

    this.assetService.createAssetCertificate(body).subscribe((data: any) => {
      this.certificates = data.assetCertificates.filter((cert) => !cert.deleted);
      Swal.fire('Success!', 'Asset Certificate has been added.', 'success');
      this.addCertificateForm.reset({ lockAssetOnExpiry: false });
      this.addCertificateModal = false;
      this.saveInProgress = false;
    });
  }

  public async onUpdateCertificateFormSubmit() {
    this.saveInProgress = true;
    const values = this.updateCertificateForm.value;

    const body = {
      assetId: this.selectedAssetId,
      title: values.title,
      expiryDate: DateTimeUtils.timelessDateAsUTCDate(values.expiryDate).toISOString(),
      lockAssetOnExpiry: values.lockAssetOnExpiry,
      certFile: this.selectedDocument
    };

    this.assetService.updateAssetCertificate(body, this.selectedCertificate.id).subscribe((data: any) => {
      this.getAsset();
      Swal.fire('Success!', 'Asset Certificate has been updated.', 'success');
      this.updateCertificateForm.reset();
      this.updateCertificateModal = false;
      this.saveInProgress = false;
    });
  }
}
